import { z } from "zod";

// Appearance of the public menu ("Aspect și personalizare"): theme, accent colour, logo and cover photo.
// Themes, accents, descriptions, the contrast formula and the diff texts are the approved prototype's
// (Meniova App.dc.html: THEMES, ACCENTS, THEME_DESC, AP0, lum/ratio, apDiff). Drafts are published separately
// from menus and from the restaurant profile, exactly like the prototype's `brand: { pub, draft }`.

export const THEME_IDS = [
  "editorial",
  "bistro",
  "cafe",
  "noapte",
  "trattoria",
  "zen",
  "brunch",
  "grill",
] as const;
export type ThemeId = (typeof THEME_IDS)[number];

export interface ThemeDef {
  label: string;
  bg: string;
  surface: string;
  ink: string;
  muted: string;
  line: string;
  chipBg: string;
  chipFg: string;
  accent: string;
  unavail: string;
  /** Display, body and price font stacks. */
  fd: string;
  fb: string;
  pf: string;
  /** Price font style and weight. */
  ps: "normal" | "italic";
  pw: number;
  nameW: number;
  nameSize: string;
  h1: string;
  h2: string;
  /** Card radius, product photo radius, chip radius. */
  r: string;
  imgR: string;
  chipR: string;
  /** Striped placeholder the prototype paints where a photo would be (used only in dashboard samples). */
  ph: string;
  /**
   * Product list: airy rows, photo cards (Bistro modern), compact lines with a mono meta line, two-column photo
   * tiles, numbered rows that open in place (accordion) or dark plates with a photo thumbnail.
   */
  layout: "rows" | "cards" | "lines" | "tiles" | "accordion" | "plates";
  /** Whether the theme shows a cover photo. */
  cover: boolean;
  /**
   * Category navigation: sticky chips, underlined tabs, a vertical rail on the left, a bordered tab strip or a
   * floating dock at the bottom of the screen (thumb-reachable).
   */
  nav: "chips" | "tabs" | "rail" | "strip" | "dock";
  /** Product details: bottom sheet, full-screen panel sliding in from the side, or opened in place (accordion). */
  detail: "sheet" | "panel" | "inline";
  /**
   * Filters on the public menu, only from real data: "allergens" hides products with a confirmed allergen,
   * "diet" keeps vegan / vegetarian products (labels) or products without gluten / milk (confirmed allergens).
   */
  filters: "none" | "allergens" | "diet";
  /**
   * How the accent is used. "text": accent text on the page and the chip label on the accent (both checked).
   * "fill": only a highlight behind ink text (active category, labels); the page's text stays in the ink colour.
   */
  accentUse: "text" | "fill";
  /** Mono font stack for overlines and meta lines (the body stack for themes without one). */
  fm: string;
  /** Mono overlines: the city above the restaurant's name, the category above a product's name. */
  kicker: boolean;
  /** Initials stand in for a missing logo (otherwise the header shows only the name). */
  initials: boolean;
  /** Search field always visible (otherwise behind the search button). */
  searchOpen: boolean;
  /** A carousel of the products the restaurant labelled "Nou", above the categories. */
  featured: boolean;
  /** Product photos in the list (otherwise only in the product's details). */
  listPhotos: boolean;
  /** Confirmed allergens on the list line (the details always show them). */
  listAllergens: boolean;
  /** A button that unfolds the category bar to show every category at once. */
  allCats: boolean;
}

/** The three original themes keep the behaviour they had before the later options existed. */
const CLASSIC = {
  nav: "chips",
  detail: "sheet",
  // Diet filters on every theme (owner 2026-11-03: „Da, la toate”); Noapte keeps its allergen filter.
  filters: "diet",
  accentUse: "text",
  kicker: false,
  initials: true,
  searchOpen: false,
  featured: false,
  listPhotos: true,
  listAllergens: false,
  allCats: false,
} as const;

/** Geist + Geist Mono (OFL): the faces of the five themes drawn after the owner's menu designs (2026-10-28). */
const GEIST = "Geist, system-ui, sans-serif";
const GEIST_MONO = "'Geist Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace";

/** Options shared by the five later themes; each sets its own navigation, details and list. */
const DESIGNED = {
  fd: GEIST,
  fb: GEIST,
  pf: GEIST,
  fm: GEIST_MONO,
  ps: "normal",
  kicker: true,
} as const;

export const THEMES: Record<ThemeId, ThemeDef> = {
  editorial: {
    label: "Editorial",
    bg: "#F4EFE7",
    surface: "#FBF8F3",
    ink: "#211C16",
    muted: "#675D51",
    line: "#DED5C7",
    chipBg: "#211C16",
    chipFg: "#F4EFE7",
    accent: "#2F5A3A",
    unavail: "#8A3B2A",
    fd: "Newsreader, Georgia, serif",
    fb: "'Hanken Grotesk', system-ui, sans-serif",
    pf: "Newsreader, Georgia, serif",
    ps: "italic",
    pw: 400,
    nameW: 500,
    nameSize: "19px",
    h1: "34px",
    h2: "26px",
    r: "10px",
    imgR: "10px",
    chipR: "999px",
    ph: "repeating-linear-gradient(135deg, #E2D7C6 0 8px, #DCD0BD 8px 16px)",
    layout: "rows",
    cover: true,
    ...CLASSIC,
    fm: "'Hanken Grotesk', system-ui, sans-serif",
  },
  bistro: {
    label: "Bistro modern",
    bg: "#FFFFFF",
    surface: "#F5F6F7",
    ink: "#15181B",
    muted: "#565D66",
    line: "#E1E4E8",
    chipBg: "#1E5B4F",
    chipFg: "#FFFFFF",
    accent: "#1E5B4F",
    unavail: "#A33A2B",
    fd: "'Schibsted Grotesk', system-ui, sans-serif",
    fb: "'Schibsted Grotesk', system-ui, sans-serif",
    pf: "'Schibsted Grotesk', system-ui, sans-serif",
    ps: "normal",
    pw: 700,
    nameW: 600,
    nameSize: "17px",
    h1: "30px",
    h2: "22px",
    r: "14px",
    imgR: "14px",
    chipR: "8px",
    ph: "repeating-linear-gradient(135deg, #E9EBED 0 8px, #E1E4E7 8px 16px)",
    layout: "cards",
    cover: false,
    ...CLASSIC,
    fm: "'Schibsted Grotesk', system-ui, sans-serif",
  },
  cafe: {
    label: "Cafenea caldă",
    bg: "#F8EFE3",
    surface: "#FFF8EE",
    ink: "#36261A",
    muted: "#6B5546",
    line: "#E6D6C2",
    chipBg: "#9A4526",
    chipFg: "#FFF8EE",
    accent: "#5E6A26",
    unavail: "#9A4526",
    fd: "'Young Serif', Georgia, serif",
    fb: "'Work Sans', system-ui, sans-serif",
    pf: "'Work Sans', system-ui, sans-serif",
    ps: "normal",
    pw: 600,
    nameW: 400,
    nameSize: "18px",
    h1: "32px",
    h2: "24px",
    r: "18px",
    imgR: "50%",
    chipR: "999px",
    ph: "repeating-linear-gradient(135deg, #EBD9C2 0 8px, #E4CFB4 8px 16px)",
    layout: "rows",
    cover: true,
    ...CLASSIC,
    fm: "'Work Sans', system-ui, sans-serif",
  },
  // The five themes below follow the owner's menu designs (reference/menu-skins-2026-10-28): colours, type,
  // spacing and navigation only. Ordering, lists for the waiter, tables, kcal, raw/cooked, doneness and wine
  // pairing are not part of Meniova and are not drawn; every value shown comes from the published menu.
  noapte: {
    ...DESIGNED,
    label: "Noapte",
    bg: "#06070A",
    surface: "#0B0D12",
    ink: "#F4F6F8",
    muted: "#B5BBC5",
    line: "#1D2026",
    chipBg: "#5277FF",
    chipFg: "#06070A",
    accent: "#5277FF",
    unavail: "#FF8A7A",
    pw: 600,
    nameW: 500,
    nameSize: "16px",
    h1: "34px",
    h2: "22px",
    r: "14px",
    imgR: "14px",
    chipR: "999px",
    ph: "repeating-linear-gradient(135deg, #12141A 0 8px, #0E1015 8px 16px)",
    layout: "lines",
    cover: false,
    nav: "chips",
    detail: "sheet",
    filters: "allergens",
    accentUse: "text",
    initials: false,
    searchOpen: true,
    featured: false,
    listPhotos: false,
    listAllergens: true,
    allCats: true,
  },
  trattoria: {
    ...DESIGNED,
    label: "Trattoria",
    bg: "#FAF3E3",
    surface: "#FFFFFF",
    ink: "#291812",
    muted: "#5C4841",
    line: "#E5DACB",
    chipBg: "#B71C0E",
    chipFg: "#FFFFFF",
    accent: "#B71C0E",
    unavail: "#9A3412",
    pw: 700,
    nameW: 600,
    nameSize: "15px",
    h1: "32px",
    h2: "24px",
    r: "20px",
    imgR: "0",
    chipR: "999px",
    ph: "repeating-linear-gradient(135deg, #F3E6D2 0 8px, #F7EDDD 8px 16px)",
    layout: "tiles",
    cover: false,
    nav: "tabs",
    detail: "sheet",
    filters: "diet",
    accentUse: "text",
    initials: false,
    searchOpen: false,
    featured: true,
    listPhotos: true,
    listAllergens: false,
    allCats: false,
  },
  zen: {
    ...DESIGNED,
    label: "Zen",
    bg: "#F8F7F2",
    surface: "#FFFFFF",
    ink: "#0E1217",
    muted: "#494D54",
    line: "#E0DFDB",
    chipBg: "#C42B00",
    chipFg: "#FFFFFF",
    accent: "#C42B00",
    unavail: "#A33A2B",
    pw: 600,
    nameW: 500,
    nameSize: "16px",
    h1: "24px",
    h2: "20px",
    r: "12px",
    imgR: "12px",
    chipR: "8px",
    ph: "repeating-linear-gradient(135deg, #EDECE6 0 8px, #F2F1EC 8px 16px)",
    layout: "lines",
    cover: false,
    nav: "rail",
    detail: "panel",
    filters: "diet",
    accentUse: "text",
    initials: true,
    searchOpen: false,
    featured: false,
    listPhotos: false,
    listAllergens: false,
    allCats: false,
  },
  brunch: {
    ...DESIGNED,
    label: "Brunch",
    bg: "#E5EFDC",
    surface: "#F9F9F0",
    ink: "#122D19",
    muted: "#2E4A34",
    line: "#BCC9B5",
    chipBg: "#F2D76C",
    chipFg: "#122D19",
    accent: "#F2D76C",
    unavail: "#8E2F1F",
    pw: 700,
    nameW: 600,
    nameSize: "18px",
    h1: "56px",
    h2: "28px",
    r: "16px",
    imgR: "16px",
    chipR: "999px",
    ph: "repeating-linear-gradient(135deg, #DCE8D1 0 8px, #E2ECD8 8px 16px)",
    layout: "accordion",
    cover: false,
    nav: "strip",
    detail: "inline",
    filters: "diet",
    accentUse: "fill",
    initials: false,
    searchOpen: false,
    featured: false,
    listPhotos: false,
    listAllergens: false,
    allCats: false,
  },
  grill: {
    ...DESIGNED,
    label: "Grill",
    bg: "#120E0C",
    surface: "#1C1714",
    ink: "#F5EDE4",
    muted: "#BFB5AE",
    line: "#2B2522",
    chipBg: "#F98F3A",
    chipFg: "#120E0C",
    accent: "#F98F3A",
    unavail: "#FF9A85",
    pw: 700,
    nameW: 600,
    nameSize: "16px",
    h1: "52px",
    h2: "28px",
    r: "18px",
    imgR: "12px",
    chipR: "16px",
    ph: "repeating-linear-gradient(135deg, #2A1F19 0 6px, #241B16 6px 12px)",
    layout: "plates",
    cover: true,
    nav: "dock",
    detail: "sheet",
    filters: "diet",
    accentUse: "text",
    initials: false,
    searchOpen: false,
    featured: false,
    listPhotos: true,
    listAllergens: false,
    allCats: false,
  },
};

/** Accent colours offered per theme: [hex, name]. The first one is the theme's default. */
export const ACCENTS: Record<ThemeId, readonly (readonly [string, string])[]> = {
  editorial: [
    ["#211C16", "Cerneală"],
    ["#2F5A3A", "Măslină"],
    ["#7A2E2A", "Vin"],
  ],
  bistro: [
    ["#1E5B4F", "Verde brad"],
    ["#1F3F7A", "Albastru"],
    ["#15181B", "Grafit"],
  ],
  cafe: [
    ["#9A4526", "Teracotă"],
    ["#5E6A26", "Oliv"],
    ["#6B3E6E", "Prună"],
  ],
  // Dark themes: the chip label is the near-black background (white on these accents is below 4.5:1).
  noapte: [
    ["#5277FF", "Albastru"],
    ["#45D5FF", "Cyan"],
    ["#7766FF", "Indigo"],
  ],
  trattoria: [
    ["#B71C0E", "Pomodoro"],
    ["#2E6537", "Busuioc"],
    ["#7A2338", "Chianti"],
  ],
  zen: [
    ["#C42B00", "Cinabru"],
    ["#2B4C7E", "Indigo"],
    ["#0E1217", "Tuș"],
  ],
  // Brunch uses the accent only as the highlight behind ink text (active category, labels), never as text.
  brunch: [
    ["#F2D76C", "Lămâie"],
    ["#F5B999", "Piersică"],
    ["#D3C6F2", "Lavandă"],
  ],
  grill: [
    ["#F98F3A", "Jar"],
    ["#E9B949", "Chihlimbar"],
    ["#FF7A66", "Măceș"],
  ],
};

export const THEME_DESC: Record<ThemeId, string> = {
  editorial:
    "Serif elegant, liste aerisite, copertă. Pentru restaurante cu meniu degustare sau carte scurtă.",
  bistro: "Sans modern, carduri cu fotografii, fără copertă. Pentru bistrouri și meniuri cu multe imagini.",
  cafe: "Serif cald, fotografii rotunde, copertă. Pentru cafenele, brutării și localuri de cartier.",
  noapte:
    "Fundal întunecat, căutare și filtru de alergeni la vedere, fotografii doar în detalii. Pentru baruri și localuri de seară.",
  trattoria:
    "Crem și roșu, carduri cu fotografii pe două coloane, noutățile în față. Pentru trattorii, pizzerii și bistrouri.",
  zen: "Minimal, categoriile pe o bară laterală, detaliile pe tot ecranul. Pentru sushi, ceainării și meniuri cu multe categorii.",
  brunch:
    "Verde pal, produse numerotate care se deschid pe loc, filtre vegan și fără gluten. Pentru brunch și cafenele de zi.",
  grill:
    "Întunecat și cald, copertă mare, categoriile jos, la îndemâna degetului. Pentru steakhouse-uri, grătare și pub-uri.",
};

export interface Appearance {
  theme: ThemeId;
  /** Uppercase #RRGGBB, always one of ACCENTS[theme]. */
  accent: string;
  /** media.id of kind "logo"; null = initials. */
  logoId: string | null;
  /** media.id of kind "cover"; null = no cover. */
  coverId: string | null;
}

/**
 * Prototype AP0 (theme Editorial, accent Cerneală) without its demo cover: a real restaurant has no cover until
 * it uploads one (DEC-032). Restaurants that never published an appearance show exactly this.
 */
export const DEFAULT_APPEARANCE: Appearance = {
  theme: "editorial",
  accent: "#211C16",
  logoId: null,
  coverId: null,
};

/** WCAG relative luminance and contrast ratio (prototype `lum` / `ratio`). */
export const lum = (hx: string): number => {
  const c = [1, 3, 5]
    .map((i) => Number.parseInt(hx.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * (c[0] ?? 0) + 0.7152 * (c[1] ?? 0) + 0.0722 * (c[2] ?? 0);
};
export const ratio = (x: string, y: string): number => {
  const p = lum(x);
  const q = lum(y);
  return (Math.max(p, q) + 0.05) / (Math.min(p, q) + 0.05);
};

/** WCAG AA for normal text: the accent is used as text on the page and behind the active chip's label. */
export const MIN_CONTRAST = 4.5;

/**
 * Contrast of the accent as text on the theme background, and of the chip label on the accent. Themes that use
 * the accent only as a fill draw that text in their ink colour, so the first value is the ink's.
 */
export function accentContrast(theme: ThemeId, accent: string): { text: number; label: number } {
  const t = THEMES[theme];
  return { text: ratio(t.accentUse === "fill" ? t.ink : accent, t.bg), label: ratio(t.chipFg, accent) };
}

/** Only the theme's verified accents, and only if both contrasts are readable (never publish unreadable text). */
export function accentAllowed(theme: ThemeId, accent: string): boolean {
  if (!ACCENTS[theme].some(([hex]) => hex === accent)) return false;
  const c = accentContrast(theme, accent);
  return c.text >= MIN_CONTRAST && c.label >= MIN_CONTRAST;
}

/** "Contrast text 14,8:1 · etichetă 14,8:1" (prototype accent rows); a fill accent carries only the label. */
export function contrastText(theme: ThemeId, accent: string): string {
  const c = accentContrast(theme, accent);
  const f = (n: number) => n.toFixed(1).replace(".", ",");
  if (THEMES[theme].accentUse === "fill") return `Fundal sub text · contrast etichetă ${f(c.label)}:1`;
  return `Contrast text ${f(c.text)}:1 · etichetă ${f(c.label)}:1`;
}

/** Choosing a theme keeps the accent when the new theme offers it, otherwise takes the theme's first accent. */
export function withTheme(a: Appearance, theme: ThemeId): Appearance {
  const keep = ACCENTS[theme].some(([hex]) => hex === a.accent);
  return { ...a, theme, accent: keep ? a.accent : (ACCENTS[theme][0]?.[0] ?? a.accent) };
}

/** "Revino la setările implicite ale temei": the theme's first accent and the default cover (none). */
export function themeDefaults(a: Appearance): Appearance {
  return { ...a, accent: ACCENTS[a.theme][0]?.[0] ?? a.accent, coverId: DEFAULT_APPEARANCE.coverId };
}

/** Logo shape per theme (prototype `logoR`; the later themes follow their designs). */
const LOGO_RADIUS: Partial<Record<ThemeId, string>> = { bistro: "14px", noapte: "12px", grill: "14px" };
export const logoRadius = (theme: ThemeId) => LOGO_RADIUS[theme] ?? "50%";

const uuid = z
  .string()
  .regex(/^[0-9a-f-]{36}$/i, "Imaginea nu mai este disponibilă. Încarc-o din nou.")
  .transform((v) => v.toLowerCase());

export const appearanceSchema = z
  .object({
    theme: z.enum(THEME_IDS, "Alege o temă."),
    accent: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, "Alege o culoare de accent.")
      .transform((v) => v.toUpperCase()),
    logoId: uuid.nullable().default(null),
    coverId: uuid.nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (!accentAllowed(v.theme, v.accent)) {
      ctx.addIssue({
        code: "custom",
        path: ["accent"],
        message: "Alege una dintre culorile verificate pentru contrast cu tema aleasă.",
      });
    }
  });

/** Canonical object with every field, in a stable key order (stored JSON and comparisons). */
export function normalizeAppearance(a: Appearance): Appearance {
  return { theme: a.theme, accent: a.accent.toUpperCase(), logoId: a.logoId, coverId: a.coverId };
}

export interface AppearanceChange {
  field: "theme" | "accent" | "logo" | "cover";
  /** Prototype diff subject: Temă, Accent, Logo, Copertă. */
  label: string;
  change: string;
}

const accentName = (theme: ThemeId, hex: string) =>
  (ACCENTS[theme] ?? []).find(([h]) => h === hex)?.[1] ?? hex;

/** Prototype `apDiff`: what publishing would change for customers. */
export function diffAppearance(published: Appearance, draft: Appearance): AppearanceChange[] {
  const out: AppearanceChange[] = [];
  if (published.theme !== draft.theme) {
    out.push({
      field: "theme",
      label: "Temă",
      change: `${THEMES[published.theme].label} → ${THEMES[draft.theme].label}`,
    });
  }
  if (published.accent !== draft.accent) {
    out.push({
      field: "accent",
      label: "Accent",
      change: `${accentName(published.theme, published.accent)} → ${accentName(draft.theme, draft.accent)}`,
    });
  }
  if (published.logoId !== draft.logoId) {
    out.push({
      field: "logo",
      label: "Logo",
      change: !draft.logoId
        ? "Eliminat (se folosesc inițialele)"
        : published.logoId
          ? "Înlocuit"
          : "Încărcat",
    });
  }
  if (published.coverId !== draft.coverId) {
    out.push({
      field: "cover",
      label: "Copertă",
      change: !draft.coverId ? "Eliminată" : published.coverId ? "Înlocuită" : "Adăugată",
    });
  }
  return out;
}

export interface AppearanceDto {
  draft: Appearance;
  /** What customers see: the last published appearance, or the default when none was ever published. */
  published: Appearance;
  revision: number;
  publishedAt: number | null;
  changes: AppearanceChange[];
}

/** Publish / discard confirm the appearance revision the user saw. */
export const appearanceRevisionSchema = z.object({ revision: z.number("Revizia lipsește.").int().min(0) });

/** Parses stored JSON defensively: anything unknown falls back to the default (never an unreadable page). */
export function parseAppearance(json: string | null | undefined): Appearance {
  if (!json) return DEFAULT_APPEARANCE;
  try {
    const v = appearanceSchema.safeParse(JSON.parse(json));
    return v.success ? normalizeAppearance(v.data) : DEFAULT_APPEARANCE;
  } catch {
    return DEFAULT_APPEARANCE;
  }
}
